/** Default table shown on first load (People / enrich.persons). */
export const DEFAULT_DQ_TABLE = 'companies';

/** Placeholder when a percentage or value is unavailable (never use em dash). */
export const EMPTY_DISPLAY = 'n/a';

// v2 schema exposes three canonical entities. People includes folded-in contact
// (email/phone) fields; placements/users/activities have no v2 home.
export const ENRICH_TABLES = [
  'companies',
  'people',
  'jobs'
];

export const TABLE_LABELS = {
  companies: 'Companies',
  people: 'People',
  jobs: 'Jobs'
};

/** valid: higher after-cleaning % is better. missing/invalid: lower is better. */
export const DQ_METRIC_KIND = {
  VALID: 'valid',
  MISSING: 'missing',
  INVALID: 'invalid'
};

/**
 * Whether a pp delta (after minus before) counts as improvement for coloring.
 * @param {number|null|undefined} deltaPp
 * @param {'valid'|'missing'|'invalid'} metricKind
 */
export function isDeltaImproved(deltaPp, metricKind) {
  if (deltaPp === null || deltaPp === undefined || Number.isNaN(Number(deltaPp))) {
    return null;
  }
  const n = Number(deltaPp);
  if (n === 0) return null;
  if (metricKind === DQ_METRIC_KIND.VALID) {
    return n > 0;
  }
  return n < 0;
}

export function deltaPpColorClass(deltaPp, metricKind) {
  const improved = isDeltaImproved(deltaPp, metricKind);
  if (improved === null) return 'text-gray-600';
  return improved ? 'text-green-700' : 'text-red-700';
}

export function formatDeltaPp(deltaPp) {
  if (deltaPp === null || deltaPp === undefined) return null;
  const n = Number(deltaPp);
  if (Number.isNaN(n)) return null;
  const prefix = n > 0 ? '+' : '';
  return `${prefix}${n.toFixed(2)} pp`;
}

/** Business-friendly labels for table/field quality status */
export const QUALITY_STATUS = {
  good: {
    label: 'Healthy',
    description: 'Most values are filled. Few remaining gaps.'
  },
  watch: {
    label: 'Needs attention',
    description: 'Enough gaps to review before you rely on this record type for lists or scoring.'
  },
  poor: {
    label: 'At risk',
    description: 'A large share of values are still missing. Likely to affect outreach and candidate scores.'
  },
  unknown: {
    label: 'Not enough data',
    description: 'Too little here to rate yet. Run a profile after your next import.'
  }
};

/** Field-level change in completeness between the compared runs (or vs the ATS import) */
export const IMPROVEMENT_STATUS = {
  improved: {
    label: 'Improved',
    description: 'More values are filled than before — SignalCue added data your ATS never had.'
  },
  unchanged: {
    label: 'No change',
    description: 'Completeness is the same as the run being compared against.'
  },
  worsened: {
    label: 'Declined',
    description: 'Fewer values are filled than before. Check the source data or a failed run.'
  },
  not_comparable: {
    label: 'Not comparable',
    description: 'There is no earlier snapshot to compare this against.'
  }
};

export function formatFieldPairName(pairName) {
  if (!pairName) return '';
  return pairName
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
